"use client";

import { useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useRefreshSessionClaims } from "@/hooks/useRefreshSessionClaims";
import type { NicepayOrderStatus } from "@/lib/nicepay-payment";

interface PaymentPollingOptions {
  orderId: string | null;
  enabled: boolean;
  onSuccess?: (data: { productId: string; productName: string; amount: number }) => void;
  onError?: (error: string) => void;
  interval?: number; // 폴링 간격 (ms), 기본값 2000ms
  maxAttempts?: number; // 최대 시도 횟수, 기본값 60 (2분)
}

interface OrderStatusResponse {
  status:
    | NicepayOrderStatus
    | "pending"
    | "payment_blocked"
    | "payment_review_required"
    | "not_found";
  orderId?: string;
  productId?: string;
  productName?: string;
  amount?: number;
  error?: string;
}

export function usePaymentPolling({
  orderId,
  enabled,
  onSuccess,
  onError,
  interval = 2000,
  maxAttempts = 60,
}: PaymentPollingOptions) {
  const router = useRouter();
  const refreshSessionClaims = useRefreshSessionClaims();
  const attemptCountRef = useRef(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // React Strict Mode 재실행이나 느린 응답으로 이전 요청이 뒤늦게 끝나도
  // 현재 폴링 실행의 성공/실패 처리는 한 번만 수행한다.
  const pollingRunRef = useRef(0);
  const terminalHandledRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    attemptCountRef.current = 0;
  }, []);

  const checkOrderStatus = useCallback(async (runId: number) => {
    if (!orderId || runId !== pollingRunRef.current || terminalHandledRef.current) {
      return;
    }

    attemptCountRef.current += 1;

    try {
      const response = await fetch(`/api/check-order-status?orderId=${encodeURIComponent(orderId)}`);
      const data: OrderStatusResponse = await response.json();

      // cleanup 전 시작된 요청이나 같은 실행에서 이미 처리된 동시 응답은 무시한다.
      if (runId !== pollingRunRef.current || terminalHandledRef.current) {
        return;
      }

      if (data.status === "paid") {
        // 결제 완료됨
        terminalHandledRef.current = true;
        stopPolling();

        // sessionClaims.publicMetadata 즉시 갱신 (JCG-1452)
        // 이용권 상태는 JWT publicMetadata 직접 조회로 통일됨 (JCG-2017) — 토큰 갱신만으로
        // 새 metadata 가 즉시 반영되므로 별도 캐시 초기화 불필요.
        // webhook이 Clerk publicMetadata를 업데이트한 직후이므로
        // 옛 JWT를 들고 다음 요청에 가지 않도록 새 토큰을 강제 발급한다.
        await refreshSessionClaims();

        if (onSuccess && data.productId && data.productName && data.amount !== undefined) {
          onSuccess({
            productId: data.productId,
            productName: data.productName,
            amount: data.amount,
          });
        }

        // 성공 페이지로 리다이렉트
        const successUrl = `/?success=true&orderId=${orderId}&openPurchases=true&amount=${data.amount}&productId=${encodeURIComponent(data.productId || "")}&productName=${encodeURIComponent(data.productName || "")}`;
        router.push(successUrl);
        return;
      }

      if (data.status === "ready") {
        terminalHandledRef.current = true;
        stopPolling();
        sessionStorage.removeItem("pendingPaymentOrderId");
        router.push(
          `/my/purchases?orderId=${encodeURIComponent(orderId)}&paymentPending=true`,
        );
        return;
      }

      if (data.status === "expired" || data.status === "cancelled") {
        terminalHandledRef.current = true;
        stopPolling();
        sessionStorage.removeItem("pendingPaymentOrderId");
        if (onError) {
          onError(
            data.status === "expired"
              ? "가상계좌 입금 기한이 만료되었습니다. 다시 결제해주세요."
              : "결제가 취소되었습니다. 다시 결제해주세요.",
          );
        }
        return;
      }

      if (data.status === "not_found") {
        // 주문이 삭제됨 (취소 또는 만료)
        terminalHandledRef.current = true;
        stopPolling();
        if (onError) {
          onError("주문 정보를 찾을 수 없습니다.");
        }
        return;
      }

      if (
        data.status === "payment_blocked" ||
        data.status === "payment_review_required"
      ) {
        terminalHandledRef.current = true;
        stopPolling();
        if (onError) {
          onError(data.error || "결제 상태를 확인해주세요.");
        }
        return;
      }

      // pending 상태 - 계속 polling
      if (attemptCountRef.current >= maxAttempts) {
        // 최대 시도 횟수 초과
        terminalHandledRef.current = true;
        stopPolling();
        if (onError) {
          onError("결제 확인 시간이 초과되었습니다. 결제가 완료되었다면 잠시 후 새로고침해주세요.");
        }
      }
    } catch (error) {
      console.error("주문 상태 확인 실패:", error);
      // 네트워크 오류 등은 무시하고 계속 polling
    }
  }, [orderId, onSuccess, onError, router, stopPolling, maxAttempts, refreshSessionClaims]);

  useEffect(() => {
    if (!enabled || !orderId) {
      pollingRunRef.current += 1;
      terminalHandledRef.current = false;
      stopPolling();
      return;
    }

    const runId = pollingRunRef.current + 1;
    pollingRunRef.current = runId;
    terminalHandledRef.current = false;

    // 즉시 한 번 확인
    void checkOrderStatus(runId);

    // 주기적으로 확인
    pollingIntervalRef.current = setInterval(() => {
      void checkOrderStatus(runId);
    }, interval);

    return () => {
      if (pollingRunRef.current === runId) {
        pollingRunRef.current += 1;
      }
      stopPolling();
    };
  }, [enabled, orderId, interval, checkOrderStatus, stopPolling]);

  return { stopPolling };
}
